import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const CourtFilingDashboard = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch('/api/filings/dashboard/', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setDashboardData(data);
        } catch (err) {
            console.error('Error fetching dashboard:', err);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'DRAFT': return 'bg-gray-100 text-gray-800';
            case 'PENDING_REVIEW': return 'bg-yellow-100 text-yellow-800';
            case 'READY_TO_FILE': return 'bg-blue-100 text-blue-800';
            case 'SUBMITTED': return 'bg-purple-100 text-purple-800';
            case 'ACCEPTED': return 'bg-green-100 text-green-800';
            case 'REJECTED': return 'bg-red-100 text-red-800';
            case 'FILED': return 'bg-green-100 text-green-800';
            case 'SERVED': return 'bg-teal-100 text-teal-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Court Filings</h1>
                <p className="text-gray-600 mt-1">Manage court filings, deadlines, and submissions</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center">
                        <div className="p-3 bg-yellow-100 rounded-lg">
                            <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Pending</p>
                            <p className="text-2xl font-semibold text-gray-900">{dashboardData?.pending_filings || 0}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center">
                        <div className="p-3 bg-blue-100 rounded-lg">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Upcoming Deadlines</p>
                            <p className="text-2xl font-semibold text-gray-900">{dashboardData?.upcoming_deadlines || 0}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center">
                        <div className="p-3 bg-red-100 rounded-lg">
                            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Overdue</p>
                            <p className="text-2xl font-semibold text-gray-900">{dashboardData?.overdue_items || 0}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center">
                        <div className="p-3 bg-green-100 rounded-lg">
                            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">This Month</p>
                            <p className="text-2xl font-semibold text-gray-900">{dashboardData?.filings_this_month || 0}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <Link
                    to="/filings/new"
                    className="flex items-center justify-center p-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    New Filing
                </Link>
                <Link
                    to="/filings/deadlines"
                    className="flex items-center justify-center p-4 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    View Deadlines
                </Link>
                <Link
                    to="/filings/courts"
                    className="flex items-center justify-center p-4 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Manage Courts
                </Link>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Recent Filings */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                    <div className="p-6 border-b border-gray-100">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-gray-900">Recent Filings</h2>
                            <Link to="/filings/all" className="text-sm text-blue-600 hover:text-blue-700">
                                View All →
                            </Link>
                        </div>
                    </div>
                    <div className="divide-y divide-gray-100">
                        {dashboardData?.recent_filings?.length > 0 ? (
                            dashboardData.recent_filings.map((filing) => (
                                <Link
                                    key={filing.id}
                                    to={`/filings/${filing.id}`}
                                    className="block p-4 hover:bg-gray-50"
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-medium text-gray-900">{filing.title}</p>
                                            <p className="text-sm text-gray-500">
                                                {filing.court_name} • {filing.case_title}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {filing.is_overdue && (
                                                <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                                                    Overdue
                                                </span>
                                            )}
                                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(filing.status)}`}>
                                                {filing.status_display}
                                            </span>
                                        </div>
                                    </div>
                                    {filing.due_date && (
                                        <p className="text-xs text-gray-400 mt-1">
                                            Due: {new Date(filing.due_date).toLocaleDateString()}
                                        </p>
                                    )}
                                </Link>
                            ))
                        ) : (
                            <div className="p-8 text-center text-gray-500">
                                <p>No filings yet</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Upcoming Deadlines */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                    <div className="p-6 border-b border-gray-100">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-gray-900">Upcoming Deadlines</h2>
                            <Link to="/filings/deadlines" className="text-sm text-blue-600 hover:text-blue-700">
                                View All →
                            </Link>
                        </div>
                    </div>
                    <div className="divide-y divide-gray-100">
                        {dashboardData?.upcoming_deadline_list?.length > 0 ? (
                            dashboardData.upcoming_deadline_list.map((deadline) => (
                                <div key={deadline.id} className="p-4">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <p className="font-medium text-gray-900">{deadline.title}</p>
                                            <p className="text-sm text-gray-500">{deadline.case_title}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className={`text-sm font-medium ${deadline.days_until_due <= 3 ? 'text-red-600' :
                                                    deadline.days_until_due <= 7 ? 'text-orange-600' :
                                                        'text-gray-600'
                                                }`}>
                                                {deadline.days_until_due === 0 ? 'Today' :
                                                    deadline.days_until_due === 1 ? 'Tomorrow' :
                                                        `${deadline.days_until_due} days`}
                                            </p>
                                            <p className="text-xs text-gray-400">
                                                {new Date(deadline.due_date).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="p-8 text-center text-gray-500">
                                <p>No upcoming deadlines</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CourtFilingDashboard;
